import React, { useState } from 'react';
import { Search, Loader, AlertCircle, ArrowRight } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { requestHumanCheck } from '@/lib/turnstile';

export default function DentalAISearch() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setAnswer(null);

    try {
      const result = await base44.functions.invoke('askDentalQuestion', {
        question: query,
        turnstile_token: await requestHumanCheck('askDentalQuestion')
      });

      setAnswer(result.data);
    } catch (err) {
      setError('Unable to find an answer. Please try another question or contact us directly.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Search Input */}
      <form onSubmit={handleSearch} className="relative">
        <input
          type="text"
          placeholder="Ask a dental question..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className="w-full px-4 py-2 pl-10 rounded-full border border-slate-200 focus:border-amber-600 focus:outline-none text-sm transition-colors bg-white"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      </form>

      {/* Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-slate-100 p-4 min-w-[320px] max-h-96 overflow-y-auto z-50">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-600 py-6">
              <Loader className="w-4 h-4 animate-spin" />
              <span className="text-sm">Searching our dental guides...</span>
            </div>
          ) : error ? (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-100">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          ) : answer ? (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 text-sm">{answer.title || 'Answer'}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{answer.answer}</p>
              <div className="flex flex-col gap-2 mt-3">
                {answer.source && (
                  <a
                    href={answer.source}
                    className="inline-flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium"
                  >
                    Read full guide
                    <ArrowRight className="w-3 h-3" />
                  </a>
                )}
                <button
                  onClick={() => window.open('/book-appointment', '_blank')}
                  className="inline-flex items-center gap-1 text-sm text-white bg-amber-600 hover:bg-amber-700 font-medium px-3 py-1.5 rounded-full transition-colors"
                >
                  Schedule Consultation
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          ) : query.trim() ? (
            <p className="text-sm text-slate-500 text-center py-4">Press Enter to search</p>
          ) : (
            <p className="text-sm text-slate-500 text-center py-4">Type a dental question to get instant answers</p>
          )}
        </div>
      )}

      {/* Close on outside click */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}